/**
 * Always-on, cheap instrumentation. ARCHITECTURE.md §12.
 *
 * Per-system frame timings live in fixed-size ring buffers so percentile queries never
 * allocate during gameplay. Counters are integers in a Map.
 */

import { nowMs } from './time.js';

const RING = 240; // ~4 seconds at 60 FPS

class Channel {
  constructor(name, budgetMs = 0) {
    this.name = name;
    this.budgetMs = budgetMs;
    this.samples = new Float32Array(RING);
    this.count = 0;
    this.head = 0;
    this.last = 0;
    this.total = 0;
    this.max = 0;
    this._sorted = new Float32Array(RING);
  }

  push(ms) {
    this.samples[this.head] = ms;
    this.head = (this.head + 1) % RING;
    if (this.count < RING) this.count++;
    this.last = ms;
    this.total += ms;
    if (ms > this.max) this.max = ms;
  }

  mean() {
    if (this.count === 0) return 0;
    let s = 0;
    for (let i = 0; i < this.count; i++) s += this.samples[i];
    return s / this.count;
  }

  /** @param {number} p percentile in [0,1] */
  percentile(p) {
    if (this.count === 0) return 0;
    const n = this.count;
    const sorted = this._sorted;
    for (let i = 0; i < n; i++) sorted[i] = this.samples[i];
    // Insertion sort: n <= 240 and this only runs on demand (HUD/profile), not per frame.
    for (let i = 1; i < n; i++) {
      const v = sorted[i];
      let j = i - 1;
      while (j >= 0 && sorted[j] > v) {
        sorted[j + 1] = sorted[j];
        j--;
      }
      sorted[j + 1] = v;
    }
    const idx = Math.min(n - 1, Math.max(0, Math.round(p * (n - 1))));
    return sorted[idx];
  }

  reset() {
    this.count = 0;
    this.head = 0;
    this.last = 0;
    this.total = 0;
    this.max = 0;
  }
}

export class MetricsRecorder {
  constructor({ enabled = true } = {}) {
    this.enabled = enabled;
    /** @type {Map<string, Channel>} */
    this.channels = new Map();
    /** @type {Map<string, number>} */
    this.counters = new Map();
    /** @type {Map<string, number>} */
    this._openTimers = new Map();
    this.frameChannel = this.channel('frame', 16.7);
    this.fixedChannel = this.channel('fixed-total', 3.0);
    this.updateChannel = this.channel('update-total', 2.0);
    this.renderChannel = this.channel('render-cpu', 1.2);
    /** Long tasks (>50 ms frames) with attribution. Bounded. */
    this.longFrames = [];
  }

  channel(name, budgetMs = 0) {
    let ch = this.channels.get(name);
    if (!ch) {
      ch = new Channel(name, budgetMs);
      this.channels.set(name, ch);
    } else if (budgetMs && !ch.budgetMs) {
      ch.budgetMs = budgetMs;
    }
    return ch;
  }

  /** Starts a named timer. Returns the start time so callers can avoid the Map on hot paths. */
  begin(name) {
    if (!this.enabled) return 0;
    const t = nowMs();
    this._openTimers.set(name, t);
    return t;
  }

  /** Ends a named timer, recording the elapsed ms. Pass `start` to skip the Map lookup. */
  end(name, start) {
    if (!this.enabled) return 0;
    const t0 = start !== undefined ? start : this._openTimers.get(name);
    if (t0 === undefined) return 0;
    const ms = nowMs() - t0;
    this.channel(name).push(ms);
    return ms;
  }

  /** Records a sample directly. */
  sample(name, ms) {
    if (!this.enabled) return;
    this.channel(name).push(ms);
  }

  counter(name, delta = 1) {
    this.counters.set(name, (this.counters.get(name) || 0) + delta);
  }

  setCounter(name, value) {
    this.counters.set(name, value);
  }

  getCounter(name) {
    return this.counters.get(name) || 0;
  }

  /** Records a >50 ms frame with the worst-offending system, bounded to 64 entries. */
  noteLongFrame(tick, ms, worstSystem, worstMs) {
    if (this.longFrames.length >= 64) this.longFrames.shift();
    this.longFrames.push({ tick, ms, worstSystem, worstMs });
  }

  /** Frames-per-second percentile derived from the frame-time channel. */
  fpsPercentile(p) {
    // High frame time == low FPS, so an FPS pXX comes from the frame-time p(1-XX).
    const ms = this.frameChannel.percentile(1 - p);
    return ms > 0 ? 1000 / ms : 0;
  }

  /** Plain-object snapshot for tools and the dev HUD. Allocates — call on demand only. */
  snapshot() {
    const systems = {};
    for (const [name, ch] of this.channels) {
      if (ch.count === 0) continue;
      systems[name] = {
        last: +ch.last.toFixed(3),
        mean: +ch.mean().toFixed(3),
        p95: +ch.percentile(0.95).toFixed(3),
        p99: +ch.percentile(0.99).toFixed(3),
        max: +ch.max.toFixed(3),
        budgetMs: ch.budgetMs,
        overBudget: ch.budgetMs > 0 && ch.percentile(0.95) > ch.budgetMs * 2,
      };
    }
    const counters = {};
    for (const [k, v] of this.counters) counters[k] = v;
    return {
      fps: {
        p50: +this.fpsPercentile(0.5).toFixed(1),
        p95: +this.fpsPercentile(0.95).toFixed(1),
        p99: +this.fpsPercentile(0.99).toFixed(1),
      },
      frameMs: {
        mean: +this.frameChannel.mean().toFixed(3),
        p95: +this.frameChannel.percentile(0.95).toFixed(3),
        p99: +this.frameChannel.percentile(0.99).toFixed(3),
        worst: +this.frameChannel.max.toFixed(3),
      },
      systems,
      counters,
      longFrames: this.longFrames.slice(),
    };
  }

  /** Histogram of frame times in 2 ms buckets up to 60 ms, plus an overflow bucket. */
  frameHistogram() {
    const buckets = new Array(32).fill(0);
    const ch = this.frameChannel;
    for (let i = 0; i < ch.count; i++) {
      const b = Math.min(31, Math.floor(ch.samples[i] / 2));
      buckets[b]++;
    }
    return buckets;
  }

  reset() {
    for (const ch of this.channels.values()) ch.reset();
    this.counters.clear();
    this.longFrames.length = 0;
  }
}
